import * as React from "react"
import { ToggleCheckbox, Link, Theme } from "gatsby-interface"
import { gatsbyCloudCdnIntegrationConfig as text } from "@modules/locales/default.js"
import { DisableCloudCdnForm } from "@modules/cdn/gatsbyCloudCdn/components/DisableCloudCdnForm"
import { FormattedMessage } from "@modules/locales"
import { useFlags } from "@modules/featureFlags"
import { useCloudCdnToggle } from "../hooks/useCloudCdnToggle"
import {
  SettingsCard,
  SettingsCardTitle,
  SettingsCardContent,
} from "./shared/SettingsCard"
import { SiteSettingsSection, SiteSettingsSectionLabel } from "../constants"
import { useSitePermissionsFieldsFragment } from "@modules/site/shared/fragments.generated"

const cardCss = (theme: Theme) => ({
  display: `grid`,
  gridGap: theme.space[7],
})

export type EnablePreviewBuildsProps = {
  siteId: string
  siteName: string
  disabled?: boolean
  onEnabledSuccess: () => void
}

export function GatsbyHostingSection({
  siteId,
  siteName,
  disabled,
  onEnabledSuccess,
}: EnablePreviewBuildsProps) {
  const { flags } = useFlags()
  const { data: permissionsData } = useSitePermissionsFieldsFragment(siteId)
  const {
    disableCloudCdn,
    enableCloudCdn,
    isConnected,
    hostingDetailsForSite,
    isUpdatingCloudCdn,
    errorAlert,
  } = useCloudCdnToggle({ siteId, onEnabledSuccess })

  const [
    disableCloudCdnRequested,
    setDisableCloudCdnRequested,
  ] = React.useState<boolean>(false)

  const [isChecked, setIsChecked] = React.useState(isConnected)

  const onToggleChange = () => {
    if (isChecked) {
      setDisableCloudCdnRequested(true)
      setIsChecked(false)
    } else {
      setIsChecked(true)
      enableCloudCdn()
    }
  }

  const onDisableCloudCdnFormDismissed = (afterSubmit?: boolean) => {
    setDisableCloudCdnRequested(false)
    if (isConnected && !afterSubmit) {
      setIsChecked(true)
    }
  }

  const onDisableCloudCdnFormSubmitted = () => {
    setIsChecked(false)
    disableCloudCdn()
  }

  React.useEffect(() => {
    setIsChecked(isConnected)
  }, [isConnected])

  return (
    <SettingsCard id={SiteSettingsSection.GatsbyHosting}>
      <SettingsCardTitle>
        {SiteSettingsSectionLabel[SiteSettingsSection.GatsbyHosting]}
      </SettingsCardTitle>
      <SettingsCardContent css={cardCss}>
        <ToggleCheckbox
          id="enable-gatsby-hosting-checkbox"
          label={text.labels.enableDeploys}
          hint={
            <React.Fragment>
              <FormattedMessage<never, "link">
                message={text.messages.questionsAboutHosting}
                tags={{
                  // @TODO we need a more direct link to the doc when it's ready
                  link: function(content) {
                    return (
                      <Link
                        href="https://support.gatsbyjs.com/hc/en-us/sections/1500000463301"
                        variant="SIMPLE"
                      >
                        {content}
                      </Link>
                    )
                  },
                }}
              />
            </React.Fragment>
          }
          checked={isChecked}
          onChange={onToggleChange}
          disabled={
            !permissionsData?.permissions?.sites?.edit ||
            disabled ||
            isUpdatingCloudCdn
          }
        />
        {errorAlert}
        {disableCloudCdnRequested && (
          <DisableCloudCdnForm
            siteName={siteName}
            onDismiss={onDisableCloudCdnFormDismissed}
            onDisable={onDisableCloudCdnFormSubmitted}
            disableRequested={disableCloudCdnRequested}
            hostingDetails={hostingDetailsForSite}
          />
        )}
      </SettingsCardContent>
    </SettingsCard>
  )
}
